/**
 * Integrytas Group - Common Security Module (CSM)
 * Common Routine - TypeScript twin (v14 standard 3.3 / 3.4).
 *
 * Every UI write and every Bulk Import row must funnel through this module.
 * It is the single entry point for Add/Change/Delete operations. The actual
 * persistence happens server-side via the `csm_common_routine` RPC defined
 * in sql/23_csm_common_routine.sql, but this module also exposes a
 * client-side dry-run validator that uses the same metadata-driven rules so
 * the UI can pre-flight a payload before sending it.
 *
 * Wire-level contract (kept identical to the SQL function):
 *
 *   {
 *     table_name:        "csm_user_master" | ...,
 *     operation:         "ADD" | "CHANGE" | "DELETE",
 *     row:               { ... column => value ... },
 *     key_columns:       ["user_id"],
 *     on_missing_lookup: "C" | "R",
 *     actor_user_id:     <bigint>
 *   }
 *
 * Result:
 *   {
 *     result_code: "OK" | "REJECTED",
 *     row_id:      <bigint | null>,
 *     created_lookups: [{table, internal_code, created_id}],
 *     warnings:    [{field, code, message}],
 *     errors:      [{field, code, message}]
 *   }
 *
 * Error codes follow the CSM.COMMON.* namespace and match the SQL routine
 * exactly so the UI can render either source identically.
 */

import { apiDb } from './api_client';

export type CommonRoutineOp = 'ADD' | 'CHANGE' | 'DELETE';
export type OnMissingLookup = 'C' | 'R';

export interface CommonRoutineMessage {
  field: string;
  code:  string;
  message: string;
}

export interface CommonRoutineCreatedLookup {
  table: string;
  internal_code: string;
  created_id: number;
}

export interface CommonRoutineRequest {
  table_name:         string;
  operation:          CommonRoutineOp;
  row:                Record<string, unknown>;
  key_columns?:       string[];
  on_missing_lookup?: OnMissingLookup;
  actor_user_id?:     number | null;
  // Audit context
  actor_ip?:          string | null;
  session_token?:     string | null;
  // Optimistic lock — the updated_at ISO string the client held at load time.
  // When provided on CHANGE operations, the server rejects with VERSION_CONFLICT
  // if the record has been modified since.
  row_version?:       string | null;
}

export interface CommonRoutineResult {
  /** PENDING_APPROVAL: the write was intercepted by the approval engine (BLOCK_STAGE
   *  mode) and queued; the row is NOT yet committed. `approval_request_id` is set. */
  result_code: 'OK' | 'REJECTED' | 'VERSION_CONFLICT' | 'PENDING_APPROVAL';
  approval_request_id?: number | null;
  row_id: number | null;
  created_lookups: CommonRoutineCreatedLookup[];
  warnings: CommonRoutineMessage[];
  errors:   CommonRoutineMessage[];
  /** Populated only when result_code === 'VERSION_CONFLICT' */
  current_version?: string;
}

/**
 * Minimal copy of the per-table metadata used by the SQL routine, so the UI
 * can perform a fast client-side dry run before round-tripping to Postgres.
 * The server is still the source of truth for the final commit.
 */
export interface CommonRoutineTableMeta {
  table_name: string;
  pk_column: string;
  required_columns: string[];
  lookup_resolvers: Record<string, {
    lookup_table: string;
    lookup_code_column: string;
    fk_column: string;
  }>;
  blocked_columns?: string[];
}

/**
 * Locally cached table meta. Populated on first use by calling
 * loadCommonRoutineMeta() once when the UI mounts. The list is small (one
 * row per registered table) so a single fetch is cheap and avoids per-row
 * round-trips during bulk import.
 */
let metaCache: Record<string, CommonRoutineTableMeta> | null = null;

export async function loadCommonRoutineMeta(): Promise<Record<string, CommonRoutineTableMeta>> {
  if (metaCache) return metaCache;
  const { data, error } = await apiDb
    .from('csm_common_routine_table_meta')
    .select('table_name, pk_column, required_columns, lookup_resolvers, blocked_columns')
    .eq('is_active', 'Y');
  if (error) {
    throw new Error(`CSM.COMMON.META_LOAD: ${error.message}`);
  }
  const map: Record<string, CommonRoutineTableMeta> = {};
  for (const row of (data ?? []) as {
    table_name: string;
    pk_column: string;
    required_columns: string[] | null;
    lookup_resolvers: Record<string, CommonRoutineTableMeta['lookup_resolvers'][string]> | null;
    blocked_columns?: string[] | null;
  }[]) {
    map[row.table_name] = {
      table_name: row.table_name,
      pk_column:  row.pk_column,
      required_columns: row.required_columns ?? [],
      lookup_resolvers: row.lookup_resolvers ?? {},
      blocked_columns:  row.blocked_columns ?? ['created_at','updated_at'],
    };
  }
  metaCache = map;
  return map;
}

/** Forget the cached metadata. Useful after editing the meta table. */
export function clearCommonRoutineMetaCache(): void { metaCache = null; }

/**
 * Pure, synchronous validator. Mirrors the early-out checks the SQL routine
 * performs - required fields and known-table check. Lookup resolution that
 * needs an actual DB query is NOT performed here; it happens during the
 * server-side commit. This is intentional so the dry-run stays a single
 * deterministic function with no I/O.
 */
export function validateCommonRoutineRequest(
  request: CommonRoutineRequest,
  meta: Record<string, CommonRoutineTableMeta>,
): CommonRoutineMessage[] {
  const errors: CommonRoutineMessage[] = [];

  if (!request.table_name) {
    errors.push({ field: 'table_name', code: 'CSM.COMMON.BAD_PAYLOAD',
      message: 'table_name is required.' });
    return errors;
  }
  const tableMeta = meta[request.table_name];
  if (!tableMeta) {
    errors.push({ field: 'table_name', code: 'CSM.COMMON.UNKNOWN_TABLE',
      message: `Table "${request.table_name}" is not registered with the Common Routine.` });
    return errors;
  }
  if (!['ADD','CHANGE','DELETE'].includes(request.operation)) {
    errors.push({ field: 'operation', code: 'CSM.COMMON.BAD_PAYLOAD',
      message: 'operation must be ADD, CHANGE, or DELETE.' });
    return errors;
  }

  // Strip blocked columns before required-field check.
  const blocked = new Set(tableMeta.blocked_columns ?? []);
  const cleanRow: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(request.row ?? {})) {
    if (!blocked.has(k)) cleanRow[k] = v;
  }

  if (request.operation === 'ADD') {
    const lookupKeys = new Set(Object.keys(tableMeta.lookup_resolvers));
    for (const required of tableMeta.required_columns) {
      const present = cleanRow[required];
      const isEmpty = present === undefined || present === null || present === '';
      if (isEmpty && !lookupKeys.has(required)) {
        errors.push({
          field: required,
          code:  'CSM.COMMON.REQUIRED_FIELD',
          message: `Field "${required}" is required.`,
        });
      }
    }
  }

  if (request.operation !== 'ADD') {
    const keys = request.key_columns ?? [];
    if (keys.length === 0) {
      errors.push({ field: 'key_columns', code: 'CSM.COMMON.BAD_PAYLOAD',
        message: 'key_columns must be provided for CHANGE/DELETE.' });
    }
    for (const k of keys) {
      const v = cleanRow[k];
      if (v === undefined || v === null || v === '') {
        errors.push({ field: k, code: 'CSM.COMMON.MISSING_KEY',
          message: `Key column "${k}" must be set.` });
      }
    }
  }

  return errors;
}

/**
 * Submit a Common Routine request to the server. The server-side function
 * is the authoritative validator (it can resolve lookups), so the result of
 * this call is the canonical outcome.
 *
 * A normalized envelope is always returned; thrown errors are wrapped as
 * `CSM.COMMON.RPC` so callers don't have to differentiate transport vs.
 * validation failures.
 */
export async function executeCommonRoutine(
  request: CommonRoutineRequest,
): Promise<CommonRoutineResult> {
  const payload: CommonRoutineRequest = {
    on_missing_lookup: 'R',
    ...request,
  };

  const { data, error } = await apiDb.rpc('csm_common_routine_with_context', {
    p_payload:       payload,
    p_ip_address:    request.actor_ip     ?? null,
    p_session_token: request.session_token ?? null,
    p_row_version:   request.row_version  ?? null,
  });
  if (error) {
    return {
      result_code: 'REJECTED',
      row_id: null,
      created_lookups: [],
      warnings: [],
      errors: [{ field: '_rpc', code: 'CSM.COMMON.RPC', message: error.message }],
    };
  }

  // The function returns a single JSONB object.
  const result = (data ?? {}) as Partial<CommonRoutineResult>;
  return {
    result_code:     result.result_code     ?? 'REJECTED',
    row_id:          result.row_id          ?? null,
    created_lookups: result.created_lookups ?? [],
    warnings:        result.warnings        ?? [],
    errors:          result.errors          ?? [],
    current_version: result.current_version,
    approval_request_id: result.approval_request_id ?? null,
  };
}
